import ChevronDown from '@assets/icons/chevron-down-v2.svg';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Animated,
  LayoutChangeEvent,
  StyleProp,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { createStyles } from '@/contexts/theme-context';
import { SvgProps } from 'react-native-svg';
import { Typography } from './typography';

type PillSize = 'small' | 'medium';

const ICON_SIZE = 16;

const PILL_HEIGHT: Record<PillSize, number> = {
  medium: 32,
  small: 24,
} as const;

// TODO: Finalize size for small count wrapper
const COUNT_WRAPPER_SIZE: Record<PillSize, { height: number; width: number }> =
  {
    medium: { height: 20, width: 20 },
    small: { height: 16, width: 16 },
  } as const;

const DROPDOWN_ICON_SIZE: Record<PillSize, { height: number; width: number }> =
  {
    medium: { height: 16, width: 16 },
    small: { height: 12, width: 12 },
  } as const;

export interface PillButtonType {
  active: boolean;
  collapsed?: boolean;
  count?: number;
  countPosition?: 'before' | 'after';
  dropDown?: boolean;
  label: string;
  onPress: () => void;
  ref?: React.RefObject<View | null>;
  style?: StyleProp<ViewStyle>;
}

interface Props {
  button: PillButtonType;
  icon?: React.FC<SvgProps>;
  onLayout?: ((event: LayoutChangeEvent) => void) | undefined;
  style?: StyleProp<ViewStyle>;
  variant?: 'small' | 'medium';
}

export const PillButton = forwardRef<View, Props>(
  ({ button, onLayout, variant = 'medium', style, icon: Icon }, ref) => {
    const [styles, theme] = useStyles();
    const collapsedAnimation = useRef(
      new Animated.Value(button.collapsed ? 0 : 1),
    ).current;
    const [animatedWidth, setAnimatedWidth] = useState<number[]>([0, 200]);

    const hasCount =
      button.count !== null && button.count !== undefined && button.count > 0;
    const countPosition = button.countPosition ?? 'before';
    const hasLeftCount = hasCount && countPosition === 'before';
    const hasRightCount = hasCount && countPosition === 'after';

    const pillHeight = PILL_HEIGHT[variant];

    useEffect(() => {
      const animation = Animated.timing(collapsedAnimation, {
        duration: 200,
        toValue: button.collapsed ? 0 : 1,
        useNativeDriver: false,
      });
      animation.start();
      return () => animation.stop();
    }, [collapsedAnimation, button.collapsed]);

    const [anchor, setAnchor] = useState({ x: 0, y: 0 });

    const onLayoutAnchor = useCallback(
      (event: LayoutChangeEvent) => {
        onLayout?.(event);
        const { width, height } = event.nativeEvent.layout;
        setAnchor({ x: width / 2, y: height / 2 - pillHeight / 2 });
      },
      [onLayout, pillHeight],
    );

    const maxWidth = collapsedAnimation.interpolate({
      inputRange: [0, 1],
      outputRange: animatedWidth,
    });

    const onLayoutButton = useCallback((event: LayoutChangeEvent) => {
      const { width: layoutWidth } = event.nativeEvent.layout;
      setAnimatedWidth([0, layoutWidth + 48]);
    }, []);

    const animatedStyle = useMemo(
      () => ({
        maxWidth,
        opacity: collapsedAnimation,
        transform: [
          { translateX: -anchor.x },
          { translateY: -anchor.y },
          { scale: collapsedAnimation },
          { translateX: anchor.x },
          { translateY: anchor.y },
        ],
      }),
      [maxWidth, collapsedAnimation, anchor],
    );

    const pillBgStyle = useMemo(
      () => ({
        backgroundColor: button.active
          ? theme.sk.color.bg.button.primary.default
          : theme.sk.color.bg.secondary.default,
      }),
      [
        button.active,
        theme.sk.color.bg.button.primary.default,
        theme.sk.color.bg.secondary.default,
      ],
    );

    const labelContentPadding = useMemo(
      () => ({
        paddingLeft: hasLeftCount
          ? 6
          : variant === 'small'
            ? theme.sk.spacing.spaceSmall
            : theme.sk.spacing.spaceMedium,
        paddingRight:
          variant === 'small'
            ? theme.sk.spacing.spaceSmall
            : theme.sk.spacing.spaceMedium,
      }),
      [
        hasLeftCount,
        variant,
        theme.sk.spacing.spaceSmall,
        theme.sk.spacing.spaceMedium,
      ],
    );

    const countWrapperSize = COUNT_WRAPPER_SIZE[variant];
    const countWrapperStyle = useMemo(
      () => ({
        height: countWrapperSize.height,
        minWidth: countWrapperSize.width,
      }),
      [countWrapperSize],
    );
    const dropDownIconSize = DROPDOWN_ICON_SIZE[variant];

    return (
      <Animated.View
        onLayout={onLayoutAnchor}
        ref={ref}
        style={[animatedStyle, button.style]}
      >
        <TouchableOpacity
          accessibilityLabel={button.label || undefined}
          accessibilityRole="button"
          activeOpacity={0.8}
          onLayout={onLayoutButton}
          onPress={button.onPress}
        >
          <View
            style={[
              styles.pill,
              styles.content,
              pillBgStyle,
              style,
              { height: pillHeight },
            ]}
          >
            {Icon ? (
              <View style={styles.iconWrapper}>
                <Icon
                  color={theme.sk.color.icon.secondary}
                  height={ICON_SIZE}
                  stroke={theme.sk.color.icon.secondary}
                  width={ICON_SIZE}
                />
              </View>
            ) : null}
            {button.label || hasCount || button.dropDown ? (
              <View style={[styles.labelContent, labelContentPadding]}>
                {hasLeftCount ? (
                  <View style={[styles.countWrapper, countWrapperStyle]}>
                    <Typography
                      color={theme.sk.color.text.secondary}
                      variant="label-sm"
                    >
                      {button.count}
                    </Typography>
                  </View>
                ) : null}
                {button.label ? (
                  <Typography
                    color={
                      button.active
                        ? theme.sk.color.text.ondark
                        : theme.sk.color.text.primary
                    }
                    variant={variant === 'small' ? 'label-sm' : 'label-md'}
                  >
                    {button.label}
                  </Typography>
                ) : null}
                {hasRightCount ? (
                  <Typography
                    color={
                      button.active
                        ? theme.sk.color.text.ondark
                        : theme.sk.color.text.primary
                    }
                    variant={variant === 'small' ? 'label-sm' : 'label-md'}
                  >
                    {` · ${button.count}`}
                  </Typography>
                ) : null}
                {button.dropDown ? (
                  <View style={[styles.dropDownIcon, dropDownIconSize]}>
                    <ChevronDown
                      stroke={
                        button.active
                          ? theme.sk.color.icon.ondark
                          : theme.sk.color.icon.primary
                      }
                    />
                  </View>
                ) : null}
              </View>
            ) : null}
          </View>
        </TouchableOpacity>
      </Animated.View>
    );
  },
);

const useStyles = createStyles((theme) => ({
  content: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
  },
  countWrapper: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.primary.default,
    borderRadius: theme.sk.radii.radiusRound,
    justifyContent: 'center',
    marginRight: theme.sk.spacing.spaceSmall,
    paddingHorizontal: theme.sk.spacing.spaceXsmall,
  },
  dropDownIcon: {
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: theme.sk.spacing.spaceXsmall,
  },
  iconWrapper: {
    padding: theme.sk.spacing.spaceSmall,
  },
  labelContent: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  pill: {
    backgroundColor: theme.sk.color.bg.secondary.default,
    borderRadius: theme.sk.radii.radiusRound,
  },
}));
